import React, { useState, useEffect, useRef } from 'react';
import { Check, Sparkles, Send } from 'lucide-react';
import clsx from 'clsx';

const ScriptPanel = ({ scenario, messages, onSendMessage, currentTip, objectivesProgress }) => {
  const [activeTab, setActiveTab] = useState('script');
  const [noteInput, setNoteInput] = useState('');
  const [completedObjectives, setCompletedObjectives] = useState([]);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // Update completed objectives from server analysis
  useEffect(() => {
    if (objectivesProgress && objectivesProgress.length > 0) {
      const completed = objectivesProgress
        .filter(obj => obj.completed || obj.suggested)
        .map(obj => obj.id);
      setCompletedObjectives(completed);
    }
  }, [objectivesProgress]);

  const handleSendNote = () => {
    if (noteInput.trim() && onSendMessage) {
      onSendMessage(noteInput);
      setNoteInput('');
    }
  };

  const toggleObjective = (index) => {
    setCompletedObjectives(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  // Auto-scroll to latest message when messages change
  useEffect(() => {
    if (activeTab === 'history' && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [messages, activeTab]);

  return (
    <div className="flex flex-col flex-1 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Tabs */}
      <div className="flex border-b border-slate-200 shrink-0">
        <button
          onClick={() => setActiveTab('script')}
          className={clsx(
            'flex-1 py-3 text-sm font-medium transition-colors',
            activeTab === 'script'
              ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
              : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'
          )}
        >
          Цели и подсказки
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={clsx(
            'flex-1 py-3 text-sm font-medium transition-colors',
            activeTab === 'history'
              ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
              : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'
          )}
        >
          История диалога
        </button>
      </div>

      {/* Content Area */}
      <div className="flex-1 min-h-0 overflow-y-auto p-6">
        {activeTab === 'script' && scenario && (
          <>
            {/* Progress Section */}
            <div className="mb-6">
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                Цели звонка
              </h3>
              <div className="space-y-3">
                {scenario.objectives?.map((objective, i) => {
                  const isCompleted = completedObjectives.includes(i);
                  const objProgress = objectivesProgress?.find(obj => obj.id === i);
                  const isSuggested = objProgress?.suggested && !isCompleted;

                  return (
                    <label
                      key={i}
                      className="flex items-start gap-3 cursor-pointer group"
                      onClick={() => toggleObjective(i)}
                    >
                      <div className={clsx(
                        "w-5 h-5 border-2 rounded flex items-center justify-center transition-all mt-0.5",
                        isCompleted
                          ? "bg-green-500 border-green-500"
                          : isSuggested
                          ? "bg-yellow-100 border-yellow-400 animate-pulse"
                          : "border-slate-300 group-hover:border-blue-400"
                      )}>
                        <Check className={clsx(
                          "w-3 h-3 text-white",
                          isCompleted ? "block" : "hidden"
                        )} />
                      </div>
                      <span className={clsx(
                        "text-sm flex-1 transition-all",
                        isCompleted
                          ? "text-slate-500 line-through"
                          : isSuggested
                          ? "text-yellow-700 font-medium"
                          : "text-slate-700"
                      )}>{objective}</span>
                      {isSuggested && !isCompleted && (
                        <span className="text-xs text-yellow-600 bg-yellow-100 px-2 py-0.5 rounded-full">
                          Готово?
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>

            {/* AI Tips */}
            <div className={clsx(
              "bg-gradient-to-br rounded-xl p-4 border transition-all duration-300",
              currentTip
                ? "from-purple-50 to-blue-50 border-purple-200 shadow-sm"
                : "from-slate-50 to-slate-50 border-slate-100"
            )}>
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className={clsx(
                  "w-4 h-4",
                  currentTip ? "text-purple-500 animate-pulse" : "text-slate-400"
                )} />
                <span className="text-xs font-semibold text-slate-500 uppercase">
                  AI Подсказка
                </span>
              </div>
              <p className="text-xs text-slate-700 leading-relaxed">
                {currentTip || 'Начните диалог - AI будет давать подсказки в процессе разговора'}
              </p>
            </div>
          </>
        )}

        {activeTab === 'history' && (
          <div ref={messagesContainerRef} className="space-y-4">
            {messages.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-8">
                История диалога пуста
              </p>
            ) : (
              <>
                {messages.map((msg, i) => (
                  <div
                    key={i}
                    className={clsx(
                      'p-3 rounded-lg',
                      msg.role === 'user'
                        ? 'bg-blue-50 border border-blue-100'
                        : 'bg-slate-50 border border-slate-100'
                    )}
                  >
                    <div className="text-xs font-semibold text-slate-500 uppercase mb-1">
                      {msg.role === 'user' ? 'Вы' : 'AI'}
                    </div>
                    <p className="text-sm text-slate-700">{msg.content}</p>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>
        )}
      </div>

      {/* Quick Input */}
      <div className="p-4 border-t border-slate-200 bg-white shrink-0">
        <div className="relative">
          <input
            type="text"
            placeholder="Заметка или текстовое сообщение..."
            value={noteInput}
            onChange={(e) => setNoteInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSendNote()}
            className="w-full pl-4 pr-10 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
          />
          <button
            onClick={handleSendNote}
            className="absolute right-2 top-2 text-slate-400 hover:text-blue-600 transition-colors"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScriptPanel;
